import { WorkflowElement } from "../../element"

import { ConditionRenderer } from "../nodes/renderers/condition"
import { EventRenderer } from "../nodes/renderers/event"
import { ActionRenderer } from "../nodes/renderers/action"
import { EndRenderer } from "../nodes/renderers/end"

import { Panel } from "../panel"

import { AsyncStats } from "../async-stats"
import { VariablesPill } from "./variables-pill"
import { Analytics } from "../../analytics"

import escape from "lodash/escape"

export class Node extends WorkflowElement {
  static get elementName() {
    return "workflow-node"
  }
  static get observedAttributes() {
    return [
      "data-extendable",
      "data-name",
      "data-detail",
      "data-kind",
      "data-can_have_exit_node"
    ]
  }

  initialize() {
    this.edit = this.edit.bind(this)
  }

  compute(roots, json) {
    this.dataset.oid = json.id
    this.dataset.kind = json.kind
    this.dataset.type = json.type
    this.dataset.typeId = json.type_id
    this.dataset.disabled = JSON.stringify(!!json.disabled)
    this.dataset.warning = JSON.stringify(
      json.type === "add-sequence" && !json.detail.available
    )
    this.dataset.updatedAt = json.updated_at
    this.dataset.name = json.name
    this.dataset.sources = (json.sources || []).join(" ")
    this.dataset.detail = JSON.stringify(json.detail)
    this.dataset.root = roots.includes(json.id)
    this.dataset.held = json.held_count > 0
    this.dataset.unstarted = json.unstarted_count > 0
    this.dataset.split_path = json.split_path

    this.dataset.can_have_exit_node = JSON.stringify(json.can_have_exit_node)

    this.dataset.allowed_action_nodes = json.allowed_action_nodes
    this.dataset.allowed_event_nodes = json.allowed_event_nodes
    this.dataset.allowed_condition_nodes = json.allowed_condition_nodes

    if (json.detail.resource_type) {
      this.dataset.resourceType = json.detail.resource_type
    }

    if (json.detail.resource_id) {
      this.dataset.resourceId = json.detail.resource_id
    }

    if (json.detail.hold_sequence) {
      this.dataset.holdSequence = json.detail.hold_sequence
    }

    if (json.detail.repeat_sequence) {
      this.dataset.repeatSequence = json.detail.repeat_sequence
    }
  }

  attributeChangedCallback(attributeName) {
    if (attributeName == "data-name" || attributeName == "data-detail") {
      this.find(".workflow-node .node-label").innerHTML = escape(
        this.renderer.text
      )

      if (this.renderer.detail.icon_url && !this.renderer.detail.app_removed) {
        fetch(this.renderer.detail.icon_url).then(response => {
          response.blob().then(blob => {
            if (blob.type == "image/svg+xml") {
              this.find(
                ".workflow-node .badge"
              ).innerHTML = `<div class="bg-white w-full h-full" style="mask: url(${escape(
                this.renderer.detail.icon_url
              )}) no-repeat center;"></div>`
            } else {
              this.find(
                ".workflow-node .badge"
              ).innerHTML = `<img src="${escape(
                this.renderer.detail.icon_url
              )}" alt="" />`
            }
          })
        })
      }
      const tooltip = this.find(
        "[data-toggle='tooltip'][data-toggle-target='badge']"
      )
      tooltip.setAttribute("data-original-title", this.renderer.tooltipText)
      tooltip.dataset.html = "true"
      $(tooltip).tooltip({
        container: "body",
        trigger: "hover"
      })
      const labelTooltip = this.find(
        "[data-toggle='tooltip'][data-toggle-target='label']"
      )
      if (this.renderer.labelTooltipText) {
        labelTooltip.setAttribute(
          "data-original-title",
          this.renderer.labelTooltipText
        )
      }
      labelTooltip.dataset.html = "true"
      $(labelTooltip).tooltip({
        container: "body",
        trigger: "hover"
      })
    }

    if (
      ["data-kind", "data-detail", "data-can_have_exit_node"].includes(
        attributeName
      )
    ) {
      switch (this.dataset.kind) {
        case "condition":
          this.renderer = new ConditionRenderer(this)
          break
        case "event":
          this.renderer = new EventRenderer(this)
          break
        case "action":
          this.renderer = new ActionRenderer(this)
          break
        case "end":
          this.renderer = new EndRenderer(this)
          break
      }
    } else if (attributeName == "data-extendable") {
      this.renderButtons()
    }
  }

  renderButtons() {
    this.findAll("workflow-add-button").forEach(button => button.remove())
    this.renderer.buttons.forEach(button => {
      this.appendChild(button)
      if (this.disabled) {
        button.disable()
      }
    })
  }

  connectedCallback() {
    if (this.disabled) {
      this.classList.add("disabled")
    }

    this.renderWarnings()
    this.renderVariablesPill()
    this.renderStats()

    this.renderButtons()
    this.find(".node-label").innerHTML = this.renderer.text

    this.renderer.render()
    if (!this.disabled) {
      this.bindEdit()
    }

    // When rendering index page there's no Workflow element
    if (typeof Workflow !== "undefined" && !this.disabled) {
      this.openEvent = new CustomEvent("va-popover:open", {
        detail: { anchor: this }
      })
      this.closeEvent = new CustomEvent("va-popover:close")
    }
  }

  renderStats() {
    if (this.find("workflow-async-stats")) {
      return
    }
    this.find(".workflow-node").appendChild(new AsyncStats(this.disabled))

    const asyncStats = this.find("workflow-async-stats")
    asyncStats.dataset.updatedAt = this.dataset.updatedAt
    asyncStats.dataset.type = this.dataset.type
    asyncStats.dataset.typeId = this.dataset.typeId
  }

  renderVariablesPill() {
    if (this.find("workflow-variables-pill")) {
      return
    }
    const graphElement = this.closest("workflow-graph")
    const graphId = graphElement?.dataset?.id || graphElement?.dataset?.oid
    if (!graphId) {
      return
    }

    const pill = new VariablesPill()
    this.find(".workflow-node").appendChild(pill)
    pill.dataset.type = this.dataset.type
    pill.dataset.nodeId = this.dataset.oid
    pill.dataset.graphId = graphId
  }

  renderWarnings() {
    if (this.find("workflow-node-warnings")) {
      return
    }

    if (this.renderer.detail.app_removed) {
      this.find(".workflow-node").classList.add("has-warning")
    }
  }

  bindEdit() {
    this.find(".workflow-node").addEventListener("click", this.edit)
  }

  edit(event) {
    event.stopPropagation()
    event.preventDefault()
    Analytics.trackNodeSelectedForEdit()

    if (
      this.dataset.root == "true" &&
      this.dataset.type == "automation-added"
    ) {
      return
    }

    const button = this.find(".btn-edit")

    Workflow.dispatchEvent(this.closeEvent)

    if (!this.detail.resource_url || button.contains(event.target)) {
      Workflow.dispatchEvent(this.openEvent)
      return
    }

    if (this.detail.resource_url) {
      const panel = new Panel()
      panel.dataset.url = this.detail.resource_url

      this.dispatchEvent(
        new CustomEvent("workflow-panel:open", {
          bubbles: true,
          detail: {
            panel: panel
          }
        })
      )

      const activeNode = document.querySelector(
        "workflow-node[data-panel-open='true']"
      )
      if (activeNode) {
        activeNode.dataset.panelOpen = false
      }

      this.dataset.panelOpen = true
    }
  }

  get detail() {
    return JSON.parse(this.dataset.detail || "null")
  }

  get can_have_exit_node() {
    return JSON.parse(this.dataset.can_have_exit_node)
  }

  get disabled() {
    return JSON.parse(this.dataset.disabled)
  }

  get warning() {
    return JSON.parse(this.dataset.warning)
  }
}

customElements.get(Node.elementName) ||
  customElements.define(Node.elementName, Node)
